import type React from 'react';
import { lazy, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/app/hooks/auth-provider';
import SecurityCheck from '@/primitives/security.check.tsx';
import { type StaffMemberModel, StaffRoleEnum } from '../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../types/user-type.ts';
import EditOrganization from './details/edit-organization.tsx';
import NewOrganization from './details/new-organization.tsx';
import OrganizationList from './list/organization-list.tsx';

const LazyOrganization = lazy(() => import('././Organization.tsx'));

export default function Organization(
  props: JSX.IntrinsicAttributes & { children?: React.ReactNode },
) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const renderView = () => {
    switch (user?.userType) {
      case UserTypeEnum.Admin:
        return <LazyOrganization {...props} />;
      case UserTypeEnum.StaffMember: {
        const staffMember = user as StaffMemberModel;
        if (
          staffMember.accountSiteStaffMembers.some(
            x => x.staffRole.id === StaffRoleEnum.OrganizationAdministrator,
          ) &&
          !pathname.includes('new')
        ) {
          return <LazyOrganization {...props} />;
        } else {
          return <Navigate to={'/dashboard'} />;
        }
      }
      case UserTypeEnum.DrugRep:
        return !pathname.includes('new') && !pathname.includes('edit') ? (
          <LazyOrganization {...props} />
        ) : (
          <Navigate to={'/dashboard'} />
        );
      default:
        return null;
    }
  };

  return <Suspense fallback={null}>{renderView()}</Suspense>;
}

export const organizationRoutes = {
  path: '/organization',
  element: <Organization />,
  children: [
    {
      path: 'new',
      element: <NewOrganization />,
    },
    {
      path: 'edit/:key',
      element: <EditOrganization />,
    },
    {
      path: '',
      element: (
        <>
          <SecurityCheck allowedUserTypes={[UserTypeEnum.Admin, UserTypeEnum.StaffMember]}>
            <OrganizationList />
          </SecurityCheck>
          <SecurityCheck allowedUserTypes={[UserTypeEnum.DrugRep]}>
            {/* redirect in case a representative has the old link bookmarked */}
            <Navigate to='/connected-accounts' />
          </SecurityCheck>
        </>
      ),
    },
  ],
};
